/**
 * services/cheatsheet.ts · v353 快捷键 cheatsheet (复用 v200 v159 TN_KEYBINDS)
 *
 * 按 ? 弹出 modal 显所有快捷键 (双语)
 * Esc 关闭 · backdrop click 关闭
 */

import { registerKey } from './keyBinds';

interface KeyBind {
  combo: string;
  zh: string;
  vi: string;
  category: '导航' | '表单' | '列表' | '其他';
}

const BINDINGS: KeyBind[] = [
  { combo: '⌘K / Ctrl+K', zh: '全局搜索 palette', vi: 'Tìm kiếm', category: '导航' },
  { combo: '?', zh: '显示快捷键 cheatsheet', vi: 'Hiện phím tắt', category: '导航' },
  { combo: 'Esc', zh: '关闭 modal / drawer / toast', vi: 'Đóng modal', category: '导航' },
  { combo: 'G H', zh: '回主页 (Home)', vi: 'Về trang chủ', category: '导航' },
  { combo: '⌘ Enter', zh: 'Form 提交', vi: 'Submit form', category: '表单' },
  { combo: '⌘ S', zh: '保存草稿', vi: 'Lưu nháp', category: '表单' },
  { combo: 'Enter', zh: '确认 confirm modal', vi: 'Xác nhận', category: '表单' },
  { combo: 'Tab', zh: '下一字段', vi: 'Trường tiếp', category: '表单' },
  { combo: 'J / K', zh: '列表下一行 / 上一行', vi: 'Hàng kế / trước', category: '列表' },
  { combo: 'G / Shift+G', zh: '列表顶 / 底', vi: 'Đầu / cuối DS', category: '列表' },
  { combo: 'Space', zh: '打开行 Drawer 预览', vi: 'Mở drawer', category: '列表' },
  { combo: 'A', zh: '勾选当前行 (批量)', vi: 'Chọn hàng', category: '列表' },
  { combo: '/', zh: '聚焦搜索框', vi: 'Focus tìm', category: '列表' },
  { combo: '⌘ /', zh: '快速命令 (v200 模块快捷)', vi: 'Lệnh nhanh', category: '其他' },
  { combo: 'F11', zh: '工厂大屏 TV mode', vi: 'Màn hình TV', category: '其他' },
];

let isOpen = false;

function ensureCss(): void {
  if (document.getElementById('tn-cheatsheet-css')) return;
  const s = document.createElement('style');
  s.id = 'tn-cheatsheet-css';
  s.textContent = `
    .tn-cheat-backdrop {
      position:fixed; inset:0; z-index:99996;
      background:rgba(0,0,0,0.5); backdrop-filter:blur(3px);
      display:flex; align-items:center; justify-content:center;
      animation:tnConfirmFade 0.15s ease-out;
    }
    .tn-cheat-card {
      background:#fff; border-radius:12px; padding:24px 28px;
      width:540px; max-width:92vw; max-height:80vh; overflow-y:auto;
      box-shadow:0 20px 40px rgba(0,0,0,0.22);
    }
    .tn-cheat-title { font-size:18px; font-weight:700; margin-bottom:6px; }
    .tn-cheat-subtitle { font-size:12px; color:#6b7280; margin-bottom:18px; }
    .tn-cheat-cat { font-size:12px; font-weight:600; color:#1e40af; text-transform:uppercase; margin:14px 0 6px; letter-spacing:0.05em; }
    .tn-cheat-row {
      display:flex; justify-content:space-between; align-items:center;
      padding:8px 0; border-bottom:1px dashed #e5e7eb;
      font-size:13px;
    }
    .tn-cheat-combo {
      font-family:ui-monospace,monospace; background:#f3f4f6;
      padding:3px 8px; border-radius:5px; border:1px solid #d1d5db;
      font-weight:600; color:#1f2937; flex-shrink:0;
    }
    .tn-cheat-desc { color:#374151; }
  `;
  document.head.appendChild(s);
}

export function showCheatsheet(): void {
  if (isOpen) return;
  isOpen = true;
  ensureCss();

  const backdrop = document.createElement('div');
  backdrop.className = 'tn-cheat-backdrop';

  const categories: Record<string, KeyBind[]> = {};
  for (const b of BINDINGS) {
    if (!categories[b.category]) categories[b.category] = [];
    categories[b.category].push(b);
  }

  const catHtml = Object.entries(categories).map(([cat, binds]) => `
    <div class="tn-cheat-cat">${escapeHtml(cat)}</div>
    ${binds.map(b => `
      <div class="tn-cheat-row">
        <span class="tn-cheat-combo">${escapeHtml(b.combo)}</span>
        <span class="tn-cheat-desc">${escapeHtml(b.zh)} · ${escapeHtml(b.vi)}</span>
      </div>
    `).join('')}
  `).join('');

  backdrop.innerHTML = `
    <div class="tn-cheat-card">
      <div class="tn-cheat-title">⌨️ 快捷键 · Phím tắt</div>
      <div class="tn-cheat-subtitle">按 Esc 或点击背景关闭 · Esc to close</div>
      ${catHtml}
    </div>
  `;
  document.body.appendChild(backdrop);

  const close = () => {
    backdrop.remove();
    document.removeEventListener('keydown', escHandler);
    isOpen = false;
  };

  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  document.addEventListener('keydown', escHandler);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
}

/** 启动时注册 ? 快捷键 */
export function initCheatsheet(): void {
  registerKey('?', () => showCheatsheet());
  // shift+? 也兼容
  registerKey('shift+/', () => showCheatsheet());
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
